import React from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';

const TermsOfService = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 backdrop-blur-md bg-white/80 border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-indigo-600" />
            <span className="text-xl font-bold text-slate-900" style={{fontFamily: 'Manrope'}}>Sponsiq</span>
          </Link>
          <Link to="/">
            <Button variant="ghost" size="sm">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Button>
          </Link>
        </div>
      </nav>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        <h1 className="text-4xl font-bold text-slate-900 mb-2" style={{fontFamily: 'Manrope'}}>Terms of Service</h1>
        <p className="text-slate-600 mb-8">Last updated: March 2026</p>

        <div className="prose prose-slate max-w-none">
          {/* Important Notice */}
          <div className="bg-indigo-50 border border-indigo-100 rounded-lg p-6 mb-8">
            <p className="text-indigo-800 font-medium mb-0">
              Sponsiq provides sponsorship intelligence based on publicly available data and automated analysis. 
              We do not guarantee sponsorship outcomes, brand partnerships, or business relationships.
            </p>
          </div>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">1. Acceptance of Terms</h2>
            <p className="text-slate-700 mb-4">
              By accessing or using Sponsiq ("the Platform"), you agree to be bound by these Terms of Service ("Terms"). 
              If you do not agree to these Terms, you may not access or use the Platform.
            </p>
            <p className="text-slate-700">
              These Terms apply to all visitors, users, and others who access or use the Platform, including creators, 
              brands, and any other parties utilizing our sponsorship intelligence services.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">2. User Accounts and Responsibilities</h2>
            <p className="text-slate-700 mb-4">
              When you create an account with us, you must provide accurate, complete, and current information. 
              Failure to do so constitutes a breach of these Terms.
            </p>
            <p className="text-slate-700 mb-4">You are responsible for:</p>
            <ul className="list-disc pl-6 text-slate-700 space-y-2">
              <li>Safeguarding your account credentials</li>
              <li>All activities that occur under your account</li>
              <li>Notifying us immediately of any unauthorized use of your account</li>
              <li>Ensuring your use of the Platform complies with all applicable laws</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">3. Platform Usage Guidelines</h2>
            <p className="text-slate-700 mb-4">You agree not to:</p>
            <ul className="list-disc pl-6 text-slate-700 space-y-2">
              <li>Use the Platform for any unlawful purpose or in violation of any applicable laws</li>
              <li>Attempt to gain unauthorized access to any part of the Platform</li>
              <li>Interfere with or disrupt the Platform or servers connected to the Platform</li>
              <li>Use automated systems or software to extract data from the Platform without permission</li>
              <li>Impersonate any person or entity or misrepresent your affiliation with any person or entity</li>
              <li>Use the Platform to send unsolicited communications or spam</li>
              <li>Reproduce, duplicate, copy, sell, or resell any portion of the Platform without express permission</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">4. Sponsorship Data Accuracy Disclaimer</h2>
            <div className="bg-amber-50 border border-amber-100 rounded-lg p-6 mb-4">
              <p className="text-amber-800 font-medium mb-0">
                Important: The sponsorship intelligence, brand recommendations, and match scores provided by Sponsiq 
                are generated through automated analysis of publicly available content and should be used for 
                informational purposes only.
              </p>
            </div>
            <p className="text-slate-700 mb-4">
              Sponsiq analyzes publicly available creator content to detect brand sponsorships and generate insights. 
              We make no representations or warranties regarding:
            </p>
            <ul className="list-disc pl-6 text-slate-700 space-y-2">
              <li>The accuracy, completeness, or reliability of sponsorship data</li>
              <li>The likelihood of securing sponsorship deals with any brand</li>
              <li>The availability or interest of any brand in sponsorship opportunities</li>
              <li>Future sponsorship trends or brand investment patterns</li>
            </ul>
            <p className="text-slate-700 mt-4">
              Brand names mentioned on this platform are for informational purposes only and do not imply 
              partnership, endorsement, or affiliation with Sponsiq.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">5. Limitation of Liability</h2>
            <p className="text-slate-700 mb-4">
              To the maximum extent permitted by applicable law, Sponsiq and its officers, directors, employees, 
              and agents shall not be liable for any indirect, incidental, special, consequential, or punitive damages, 
              including but not limited to:
            </p>
            <ul className="list-disc pl-6 text-slate-700 space-y-2">
              <li>Loss of profits, revenue, or business opportunities</li>
              <li>Loss of data or goodwill</li>
              <li>Failed or unsuccessful sponsorship negotiations</li>
              <li>Any damages arising from your reliance on sponsorship intelligence data</li>
            </ul>
            <p className="text-slate-700 mt-4">
              Our total liability for any claims arising from or related to your use of the Platform shall not 
              exceed the amount you paid to us in the twelve (12) months preceding the claim.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">6. Intellectual Property Notice</h2>
            <p className="text-slate-700 mb-4">
              The Platform and its original content, features, and functionality are owned by Sponsiq and are 
              protected by international copyright, trademark, patent, trade secret, and other intellectual 
              property laws.
            </p>
            <p className="text-slate-700">
              You may not copy, modify, distribute, sell, or lease any part of our Platform or included software, 
              nor may you reverse engineer or attempt to extract the source code of that software.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">7. Termination Rights</h2>
            <p className="text-slate-700 mb-4">
              We may terminate or suspend your account and access to the Platform immediately, without prior 
              notice or liability, for any reason, including but not limited to:
            </p>
            <ul className="list-disc pl-6 text-slate-700 space-y-2">
              <li>Breach of these Terms</li>
              <li>Fraudulent or illegal activity</li>
              <li>Conduct that may harm other users or the Platform</li>
              <li>Extended periods of inactivity</li>
            </ul>
            <p className="text-slate-700 mt-4">
              Upon termination, your right to use the Platform will immediately cease. All provisions of these 
              Terms which by their nature should survive termination shall survive.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">8. Changes to Terms</h2>
            <p className="text-slate-700 mb-4">
              We reserve the right to modify or replace these Terms at any time. If a revision is material, 
              we will provide at least 30 days' notice prior to any new terms taking effect.
            </p>
            <p className="text-slate-700">
              By continuing to access or use our Platform after those revisions become effective, you agree 
              to be bound by the revised Terms. If you do not agree to the new Terms, please stop using the Platform.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">9. Contact Information</h2>
            <p className="text-slate-700">
              If you have any questions about these Terms, please contact us at{' '}
              <a href="mailto:hello.sponsiq@gmail.com" className="text-indigo-600 hover:text-indigo-800">
                hello.sponsiq@gmail.com
              </a>
            </p>
          </section>
        </div>

        {/* Footer Links */}
        <div className="mt-12 pt-8 border-t border-slate-200 flex flex-wrap gap-6 text-sm text-slate-600">
          <Link to="/privacy" className="hover:text-indigo-600 transition-colors">Privacy Policy</Link>
          <Link to="/" className="hover:text-indigo-600 transition-colors">Back to Home</Link>
        </div>
      </main>
    </div>
  );
};

export default TermsOfService;
